import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftRight,
  Camera,
  Compass,
  History,
  LayoutDashboard,
  Trophy,
  Gift,
} from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import ConnectButton from '@/components/profile/ConnectButton';
import Rail1LinkBadge from '@/components/profile/Rail1LinkBadge';
import { useParticipantProfile } from '@/hooks/useParticipantProfile';
import { useUiStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { cn, formatPoints, truncateAddress } from '@/lib/utils';

interface NavDef {
  to: string;
  label: string;
  Icon: typeof Compass;
  end?: boolean;
}

const NAV: NavDef[] = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/', label: 'Missions', Icon: Compass, end: true },
  { to: '/submit', label: 'Submit', Icon: Camera },
  { to: '/leaderboard', label: 'Leaderboard', Icon: Trophy },
  { to: '/history', label: 'History', Icon: History },
  { to: '/referrals', label: 'Referrals', Icon: Gift },
  { to: '/conversion', label: 'Conversion', Icon: ArrowLeftRight },
];

export interface SidebarProps {
  /** Called whenever a nav link is activated — used by the mobile drawer to close itself. */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profile = useAuthStore((s) => s.profile);
  const { data: participant } = useParticipantProfile();
  const openProfile = useUiStore((s) => s.openProfile);

  return (
    <aside
      className="flex h-screen w-[260px] shrink-0 flex-col border-r border-line bg-white"
      aria-label="Primary navigation"
    >
      {/* Brand — the full BigWater wordmark (links to the marketing home) +
          "Quests" eyebrow tag below it. */}
      <div className="flex flex-col items-start gap-2 px-5 py-5">
        <a
          href="https://bigwater.io"
          aria-label="BigWater home"
          className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-500"
        >
          <img
            src="/full-logo.svg"
            alt="BigWater"
            className="h-8 w-auto"
            width={160}
            height={40}
          />
        </a>
        <span className="eyebrow eyebrow-water font-mono text-[10px] uppercase">
          Quests
        </span>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1 px-3 pt-2" aria-label="Primary">
        {NAV.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                isActive ? 'nav-item-active' : 'nav-item',
                'relative min-h-[44px] overflow-hidden',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive ? (
                  <motion.span
                    layoutId="sidebar-active-rail"
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-water-500 to-water-700"
                    transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                  />
                ) : null}
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Signed out: the connect CTA sits directly under the nav, set off by a
          hairline divider (the app has no top header). */}
      {!(isAuthenticated && profile) ? (
        <div className="mt-2 border-t border-line px-3 pt-3">
          <ConnectButton />
        </div>
      ) : null}

      {/* Signed in: the profile chip anchors to the foot of the sidebar. */}
      {isAuthenticated && profile ? (
        <div className="mt-auto border-t border-line px-4 py-4">
          <button
            type="button"
            onClick={() => {
              onNavigate?.();
              openProfile();
            }}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border border-line bg-paper-warm px-3 py-2.5 text-left',
              'transition-colors duration-150 hover:border-water-200 hover:bg-water-50',
              'min-h-[44px]',
            )}
          >
            <Avatar src={profile.avatarUrl} name={profile.displayName} size={36} />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-display text-sm font-semibold text-ink-950">
                {profile.displayName || truncateAddress(profile.address)}
              </span>
              <span className="num-tabular text-xs text-slate2-500">
                <span className="font-mono">{truncateAddress(profile.address)}</span>
                {participant ? (
                  <>
                    {' · '}
                    {formatPoints(participant.score)} pts
                  </>
                ) : null}
              </span>
            </span>
          </button>
          <Rail1LinkBadge className="mt-2" />
        </div>
      ) : null}
    </aside>
  );
}

export default Sidebar;
